import { Button, Icon, PageBody } from '@pypestream/design-system';
import { FC, ReactNode } from 'react';

import { NoAccessIllustration, ToolLoaderCard } from './access-setup-artwork';
import { ACCESS_SETUP_SCREEN_CSS } from './access-setup-screen.styles';
import { AccessSetupScreenProps } from './types';

interface AccessFrameProps {
  media: ReactNode;
  /** Absent while loading: the row is kept, empty, so the rows stay aligned. */
  headline?: string;
  body: ReactNode;
  actions: ReactNode;
  testId?: string;
  /** The async setup is still in flight (WCAG 4.1.3 — mark the view busy). */
  busy?: boolean;
  /**
   * Announce the body assertively (a terminal outcome) rather than politely
   * (the in-progress status). `role="alert"` is announced even when the region
   * is inserted together with its text, which is what happens when the screen
   * goes from loading to denied: the media changes element type and React
   * remounts the node, so a polite region would not be announced (WCAG 4.1.3).
   */
  alert?: boolean;
}

/**
 * The frame shared by all three states: media (220) · headline (36, empty on
 * loading) · body (28) · gap (32) · actions (48). One frame keeps the rows on
 * the same baselines whichever state is showing.
 */
const AccessFrame: FC<AccessFrameProps> = ({
  media,
  headline,
  body,
  actions,
  testId,
  busy,
  alert,
}) => (
  <PageBody className="c-access-setup-page" background="none" spacing="none">
    <div
      className="c-access-setup"
      data-testid={testId}
      aria-busy={busy || undefined}
    >
      {/* The stylesheet travels with the component; see the styles module for
          why it is not a `.scss` import. `display: none` by default, so it
          takes no part in the flex layout around it. */}
      <style>{ACCESS_SETUP_SCREEN_CSS}</style>
      <div className="c-access-setup__view">
        <div className="c-access-setup__media">{media}</div>
        <div className="c-access-setup__copy">
          {headline ? (
            <h1 className="c-access-setup__headline">{headline}</h1>
          ) : (
            <div
              className="c-access-setup__headline c-access-setup__headline--empty"
              aria-hidden="true"
            />
          )}
          <p className="c-access-setup__body" role={alert ? 'alert' : 'status'}>
            {body}
          </p>
        </div>
        <div className="c-access-setup__actions">{actions}</div>
      </div>
    </div>
  </PageBody>
);

/**
 * The screen shown while a user is being given access to a project, and the
 * two outcomes that can follow: `loading` (the assignment is running),
 * `failed` (it could not be completed — offers Retry) and `denied` (the user
 * cannot be given access — offers Home).
 *
 * Presentation only. Which status to show, what the actions do and where they
 * lead are the app's decisions, and every string arrives through `labels`.
 * The screen renders at most one `h1` and exactly one live region: the body,
 * `role="status"` while loading and `role="alert"` once the outcome is known.
 */
export const AccessSetupScreen: FC<AccessSetupScreenProps> = (props) => {
  const { labels, onMyAccount } = props;

  if (props.status === 'loading') {
    return (
      <AccessFrame
        busy
        media={<ToolLoaderCard />}
        body={
          <>
            {labels.loading.body}
            <span className="c-access-setup__dots" aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </>
        }
        actions={<p className="c-access-setup__note">{labels.loading.note}</p>}
      />
    );
  }

  const myAccount = (testId: string) => (
    <Button
      variant="secondary"
      size="xlarge"
      onClick={onMyAccount}
      test-id={testId}
    >
      <Icon slot="prefix" name="avatar" size="xsmall" />
      {labels.actions.myAccount}
    </Button>
  );

  if (props.status === 'failed') {
    return (
      <AccessFrame
        alert
        testId="access-setup-failed"
        media={<ToolLoaderCard failed />}
        headline={labels.failed.headline}
        body={labels.failed.body}
        actions={
          <>
            {myAccount('access-setup-failed-account')}
            <Button
              size="xlarge"
              onClick={props.onRetry}
              test-id="access-setup-retry"
            >
              <Icon slot="prefix" name="refresh" size="xsmall" />
              {labels.actions.retry}
            </Button>
          </>
        }
      />
    );
  }

  return (
    <AccessFrame
      alert
      testId="access-setup-denied"
      media={<NoAccessIllustration />}
      headline={labels.denied.headline}
      body={labels.denied.body}
      actions={
        <>
          {myAccount('access-setup-denied-account')}
          <Button
            size="xlarge"
            onClick={props.onHome}
            test-id="access-setup-home"
          >
            {/* Figma "Button — Home" (25501:28990) carries the Profile glyph, same as My Account. */}
            <Icon slot="prefix" name="avatar" size="xsmall" />
            {labels.actions.home}
          </Button>
        </>
      }
    />
  );
};
